import NavRail from '@/components/shared/NavRail';
import PageTabBar from '@/components/shared/PageTabBar';
import { API_ENDPOINTS } from '@/constants';
import { isPremium } from '@/helpers/helpers';
import { getApiService } from '@/services/apiService';
import { ApiResponse } from '@/types';
import { reportAppError } from '@/helpers/sentry';
import {
  ArrowPathIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  GlobeAltIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// ─── Types ───────────────────────────────────────────────────

interface StripeInfo {
  id: string;
  status: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  plan: string;
  interval: string;
  amount: number;
  currency: string;
}

interface GoldSubscriptionInfo {
  type: string;
  expiration: number;
}

interface SnipeItemData {
  itemId: number;
  price: number;
}

interface SnipeConfigData {
  enabled: boolean;
  realms: number[];
  items: SnipeItemData[];
  pets: SnipeItemData[];
  alertStyle?: string;
}

interface WowAccount {
  id: string;
  name: string;
  createdAt: string;
}

interface RealmConfig {
  realmId: number;
  accountId: string;
}

interface RegionConfig {
  buyingRealms: RealmConfig[];
  sellingRealms: RealmConfig[];
}

interface DiscordNotification {
  regionUpdate: boolean;
}

interface ConfigData {
  categories: string[];
  qualities: number[];
  expansions: number[];
  sellRate: number[];
  saleAvg: number[];
  saleAvgVsBuyPercent: number[];
  goldProfit: number[];
  goldProfitSaleAvg: number[];
  goldProfitPercent: number[];
  maxGoldBuyout: number[];
  ilvl: number[];
}

interface DataConfiguration {
  dropShipCount: number;
  storedFilters: number;
  sellingRealms: number;
}

interface UserLookupData {
  id: string;
  email: string;
  role: string;
  region: string | null;
  acceptedTerms: boolean;
  createdAt: string;
  subscriptionStatus: string;
  subscriptionType: string;
  goldSubscription: GoldSubscriptionInfo | null;
  stripeSubscription: StripeInfo | null;
  discordId: string | null;
  discordUsername: string | null;
  discordDiscriminator: string | null;
  discordNotification: DiscordNotification | null;
  wowAccounts: WowAccount[];
  retailConfigUS: RegionConfig | null;
  retailConfigEU: RegionConfig | null;
  config: ConfigData | null;
  dataConfiguration: DataConfiguration | null;
  autoRefresh: boolean;
  favorites: string[];
  snipeConfig: SnipeConfigData | null;
  accessType: string;
  trialStatus: {
    hasTrial: boolean;
    trialActive: boolean;
    trialExpiresAt: string | null;
    daysRemaining: number | null;
  } | null;
}

// ─── Helpers ─────────────────────────────────────────────────

const formatDate = (value: string | number | null | undefined): string => {
  if (!value) return '—';
  const d = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  if (isNaN(d.getTime())) {
    // Try as unix ms
    const d2 = new Date(Number(value));
    if (isNaN(d2.getTime())) return String(value);
    return d2.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDateMs = (ms: number | null | undefined): string => {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatGold = (copper: number): string => {
  const gold = Math.floor(copper / 10000);
  const silver = Math.floor((copper % 10000) / 100);
  return `${gold.toLocaleString()}g ${silver}s`;
};

const statusColor = (status: string): string => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800 border-green-300';
    case 'in_cancelation': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'expired': return 'bg-red-100 text-red-800 border-red-300';
    default: return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

const roleColor = (role: string): string => {
  switch (role) {
    case 'admin': return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'moderator': return 'bg-blue-100 text-blue-800 border-blue-300';
    default: return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

// ─── Sub-components ──────────────────────────────────────────

const Badge: React.FC<{ text: string; className?: string }> = ({ text, className = '' }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${className}`}>
    {text}
  </span>
);

const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-gray-900 font-semibold text-sm">
          {icon}
          {title}
        </div>
        <svg
          className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-5 py-4">{children}</div>}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 font-medium">{label}</span>
    <span className="text-sm text-gray-900 text-right max-w-[60%] break-all">{value ?? '—'}</span>
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-sm text-gray-400 italic">{text}</p>
);

// ─── Main Component ──────────────────────────────────────────

const UserLookup: React.FC = () => {
  const api = getApiService();
  const { t } = useTranslation(['common']);
  const [searchEmail, setSearchEmail] = useState('');
  const [user, setUser] = useState<UserLookupData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [tokenPreview, setTokenPreview] = useState<Record<string, unknown> | null>(null);
  const [tokenPreviewLoading, setTokenPreviewLoading] = useState(false);
  const [tokenPreviewCopied, setTokenPreviewCopied] = useState(false);
  // Stable "now" snapshot captured at mount; admin lookup doesn't need live updates.
  const [nowMs] = useState<number>(() => Date.now());

  // Email-change form state
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  const startEmailEdit = () => {
    setEmailDraft(user?.email ?? '');
    setEmailEditing(true);
  };

  const cancelEmailEdit = () => {
    setEmailEditing(false);
    setEmailDraft('');
  };

  const submitEmailChange = async () => {
    if (!user) return;
    const next = emailDraft.trim().toLowerCase();
    if (!next || next === user.email.toLowerCase()) {
      cancelEmailEdit();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!window.confirm(
      `Change email from "${user.email}" to "${next}" and send a password-reset email to the new address?`
    )) return;

    setEmailSaving(true);
    try {
      const response = await api.put(
        `${API_ENDPOINTS.ADMIN.CHANGE_USER_EMAIL}${encodeURIComponent(user.id)}/email`,
        JSON.stringify({ email: next }),
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.error || 'Failed to change email');
        return;
      }
      toast.success(data.status || 'Email changed');
      setUser({ ...user, email: data.email ?? next });
      setSearchEmail(data.email ?? next);
      cancelEmailEdit();
    } catch (err) {
      reportAppError('Failed to change user email', err);
      toast.error('Failed to change email');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const email = searchEmail.trim();
    if (!email) return;

    setLoading(true);
    setSearched(true);
    setUser(null);
    setTokenPreview(null);

    try {
      const response = await api.get(`${API_ENDPOINTS.ADMIN.USER_LOOKUP}${encodeURIComponent(email)}`);
      if (response.ok) {
        const data: ApiResponse<UserLookupData> = await response.json();
        setUser(data.data);

        // Fetch token preview in parallel
        setTokenPreviewLoading(true);
        api.get(`${API_ENDPOINTS.ADMIN.TOKEN_PREVIEW}${encodeURIComponent(email)}`)
          .then(async (r) => {
            if (r.ok) {
              const d = await r.json();
              setTokenPreview(d.data);
            } else {
              setTokenPreview(null);
            }
          })
          .catch(() => setTokenPreview(null))
          .finally(() => setTokenPreviewLoading(false));

        toast.success('User found');
      } else if (response.status === 404) {
        toast.error('User not found');
      } else {
        const err = await response.json();
        toast.error(err.message || 'Lookup failed');
      }
    } catch (error) {
      reportAppError('Error looking up user', error);
      toast.error('Error looking up user');
    } finally {
      setLoading(false);
    }
  };

  const adminTabs = [
    { label: t('common:adminItems.stats'), path: '/admin/stats' },
    { label: t('common:adminItems.userInfo'), path: '/admin/user' },
    { label: t('common:adminItems.itemLookup'), path: '/admin/item' },
    { label: t('common:adminItems.goldSubscription'), path: '/admin/gold' },
    { label: t('common:adminItems.snipeAudit'), path: '/admin/snipe-audit' },
    { label: t('common:adminItems.emailPreviews'), path: '/admin/emails' },
  ];

  return (
    <div className="flex overflow-hidden h-full">
      <NavRail />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <PageTabBar tabs={adminTabs} />
        <div className="flex-1 overflow-y-auto scrollbar-styled">
    <div className="py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* ── Search Bar ── */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <h1 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MagnifyingGlassIcon className="h-6 w-6 text-blue-600" />
            User Lookup
          </h1>
          <form onSubmit={handleSearch} className="flex gap-3">
            <input
              type="text"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="Enter user email…"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={loading || !searchEmail.trim()}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              {loading ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <MagnifyingGlassIcon className="h-4 w-4" />
              )}
              Search
            </button>
          </form>
        </div>

        {/* ── No result ── */}
        {searched && !loading && !user && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <UserCircleIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No user found for that email.</p>
          </div>
        )}

        {/* ── User Data ── */}
        {user && (
          <div className="space-y-4">

            {/* ── Header Card ── */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{user.email}</h2>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">ID: {user.id}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge text={user.subscriptionStatus.replace('_', ' ')} className={statusColor(user.subscriptionStatus)} />
                  <Badge text={user.role || 'user'} className={roleColor(user.role)} />
                  {isPremium(user.accessType) && <Badge text="Premium" className="bg-amber-100 text-amber-800 border-amber-300" />}
                  {user.region && <Badge text={user.region.toUpperCase()} className="bg-blue-50 text-blue-700 border-blue-200" />}
                  {user.trialStatus?.trialActive && <Badge text="Trial" className="bg-emerald-100 text-emerald-800 border-emerald-300" />}
                </div>
              </div>
            </div>

            {/* ── Account Info ── */}
            <SectionCard title="Account" icon={<UserCircleIcon className="h-4 w-4 text-gray-500" />}>
              <InfoRow
                label="Email"
                value={
                  emailEditing ? (
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      <input
                        type="email"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        disabled={emailSaving}
                        className="border border-gray-300 rounded px-2 py-1 text-sm font-mono w-64 max-w-full"
                        placeholder="new.email@example.com"
                      />
                      <button
                        type="button"
                        onClick={submitEmailChange}
                        disabled={emailSaving}
                        className="px-2 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-300 text-white rounded text-xs"
                      >
                        {emailSaving ? 'Saving…' : 'Save & send reset'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEmailEdit}
                        disabled={emailSaving}
                        className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 justify-end">
                      <span>{user.email}</span>
                      <button
                        type="button"
                        onClick={startEmailEdit}
                        className="px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs border border-gray-200"
                        title="Change email and send password reset"
                      >
                        Edit
                      </button>
                    </div>
                  )
                }
              />
              <InfoRow label="Role" value={user.role || 'user'} />
              <InfoRow label="Region" value={user.region ? user.region.toUpperCase() : '—'} />
              <InfoRow label="Accepted Terms" value={user.acceptedTerms ? 'Yes' : 'No'} />
              <InfoRow label="Auto Refresh" value={user.autoRefresh ? 'Enabled' : 'Disabled'} />
              <InfoRow label="Created At" value={formatDate(user.createdAt)} />
            </SectionCard>

            {/* ── Subscription ── */}
            <SectionCard title="Subscription" icon={<CreditCardIcon className="h-4 w-4 text-gray-500" />}>
              <InfoRow label="Status" value={<Badge text={user.subscriptionStatus.replace('_', ' ')} className={statusColor(user.subscriptionStatus)} />} />
              <InfoRow label="Type" value={user.subscriptionType || '—'} />

              {user.goldSubscription && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Gold Subscription</p>
                  <InfoRow label="Plan" value={user.goldSubscription.type} />
                  <InfoRow label="Expires" value={formatDateMs(user.goldSubscription.expiration)} />
                  <InfoRow label="Expired?" value={user.goldSubscription.expiration < nowMs ? 'Yes' : 'No'} />
                </div>
              )}

              {user.stripeSubscription && (
                <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wide mb-2">Stripe Subscription</p>
                  <InfoRow label="Stripe ID" value={<span className="font-mono text-xs">{user.stripeSubscription.id}</span>} />
                  <InfoRow label="Status" value={user.stripeSubscription.status} />
                  <InfoRow label="Plan" value={user.stripeSubscription.plan} />
                  <InfoRow label="Interval" value={user.stripeSubscription.interval} />
                  <InfoRow label="Amount" value={`${(user.stripeSubscription.amount / 100).toFixed(2)} ${user.stripeSubscription.currency.toUpperCase()}`} />
                  <InfoRow label="Period Start" value={formatDate(user.stripeSubscription.currentPeriodStart)} />
                  <InfoRow label="Period End" value={formatDate(user.stripeSubscription.currentPeriodEnd)} />
                  <InfoRow label="Cancel at Period End" value={user.stripeSubscription.cancelAtPeriodEnd ? 'Yes' : 'No'} />
                </div>
              )}

              {!user.goldSubscription && !user.stripeSubscription && (
                <EmptyState text="No active subscription" />
              )}
            </SectionCard>

            {/* ── Discord ── */}

            {/* -- Trial Info -- */}
            <SectionCard title="Trial" icon={<ShieldCheckIcon className="h-4 w-4 text-gray-500" />}>
              {user.trialStatus?.hasTrial ? (
                <>
                  <InfoRow label="Trial Used" value="Yes" />
                  <InfoRow label="Status" value={
                    user.trialStatus.trialActive
                      ? <Badge text="Active" className="bg-green-100 text-green-800 border-green-300" />
                      : <Badge text="Expired" className="bg-red-100 text-red-800 border-red-300" />
                  } />
                  {user.trialStatus.trialExpiresAt && (
                    <InfoRow label="Expires" value={formatDate(user.trialStatus.trialExpiresAt)} />
                  )}
                  {user.trialStatus.trialActive && user.trialStatus.daysRemaining !== null && (
                    <InfoRow label="Days Remaining" value={user.trialStatus.daysRemaining} />
                  )}
                </>
              ) : (
                <EmptyState text="No trial used" />
              )}
            </SectionCard>

            <SectionCard title="Discord" icon={<ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-500" />}>
              {user.discordId ? (
                <>
                  <InfoRow label="Discord ID" value={<span className="font-mono text-xs">{user.discordId}</span>} />
                  <InfoRow label="Username" value={user.discordUsername || '—'} />
                  <InfoRow label="Discriminator" value={user.discordDiscriminator || '—'} />
                  {user.discordNotification && (
                    <InfoRow label="Region Update Notifications" value={user.discordNotification.regionUpdate ? 'Enabled' : 'Disabled'} />
                  )}
                </>
              ) : (
                <EmptyState text="Discord not linked" />
              )}
            </SectionCard>

            {/* ── WoW Accounts ── */}
            <SectionCard title="WoW Accounts" icon={<ComputerDesktopIcon className="h-4 w-4 text-gray-500" />}>
              {user.wowAccounts && user.wowAccounts.length > 0 ? (
                <div className="space-y-2">
                  {user.wowAccounts.map((acc) => (
                    <div key={acc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{acc.name}</p>
                        <p className="text-xs text-gray-400 font-mono">{acc.id}</p>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(acc.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="No WoW accounts" />
              )}
            </SectionCard>

            {/* ── Realm Configs ── */}
            <SectionCard title="Realm Configuration" icon={<GlobeAltIcon className="h-4 w-4 text-gray-500" />}>
              {(['US', 'EU'] as const).map((regionLabel) => {
                const config = regionLabel === 'US' ? user.retailConfigUS : user.retailConfigEU;
                const buying = config?.buyingRealms || [];
                const selling = config?.sellingRealms || [];
                if (buying.length === 0 && selling.length === 0) return null;

                return (
                  <div key={regionLabel} className="mb-4 last:mb-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{regionLabel} Region</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs font-medium text-green-700 mb-1">Buying Realms ({buying.length})</p>
                        {buying.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {buying.map((r, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-mono">
                                {r.realmId}
                                {r.accountId && <span className="text-green-500 ml-1">({r.accountId.slice(0, 6)}…)</span>}
                              </span>
                            ))}
                          </div>
                        ) : <EmptyState text="None" />}
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-xs font-medium text-orange-700 mb-1">Selling Realms ({selling.length})</p>
                        {selling.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {selling.map((r, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-mono">
                                {r.realmId}
                                {r.accountId && <span className="text-orange-500 ml-1">({r.accountId.slice(0, 6)}…)</span>}
                              </span>
                            ))}
                          </div>
                        ) : <EmptyState text="None" />}
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!user.retailConfigUS?.buyingRealms?.length && !user.retailConfigUS?.sellingRealms?.length &&
                !user.retailConfigEU?.buyingRealms?.length && !user.retailConfigEU?.sellingRealms?.length) && (
                  <EmptyState text="No realm configuration" />
                )}
            </SectionCard>

            {/* ── Filter Config ── */}
            <SectionCard title="Filter Configuration" icon={<Cog6ToothIcon className="h-4 w-4 text-gray-500" />} defaultOpen={false}>
              {user.config ? (
                <>
                  <InfoRow label="Categories" value={user.config.categories?.length ? user.config.categories.join(', ') : '—'} />
                  <InfoRow label="Qualities" value={user.config.qualities?.length ? user.config.qualities.join(', ') : '—'} />
                  <InfoRow label="Expansions" value={user.config.expansions?.length ? user.config.expansions.join(', ') : '—'} />
                  <InfoRow label="Sell Rate" value={user.config.sellRate?.length ? user.config.sellRate.join(' – ') : '—'} />
                  <InfoRow label="Sale Avg" value={user.config.saleAvg?.length ? user.config.saleAvg.join(' – ') : '—'} />
                  <InfoRow label="Sale Avg vs Buy %" value={user.config.saleAvgVsBuyPercent?.length ? user.config.saleAvgVsBuyPercent.join(' – ') : '—'} />
                  <InfoRow label="Gold Profit" value={user.config.goldProfit?.length ? user.config.goldProfit.join(' – ') : '—'} />
                  <InfoRow label="Gold Profit (Sale Avg)" value={user.config.goldProfitSaleAvg?.length ? user.config.goldProfitSaleAvg.join(' – ') : '—'} />
                  <InfoRow label="Gold Profit %" value={user.config.goldProfitPercent?.length ? user.config.goldProfitPercent.join(' – ') : '—'} />
                  <InfoRow label="Max Gold Buyout" value={user.config.maxGoldBuyout?.length ? user.config.maxGoldBuyout.join(' – ') : '—'} />
                  <InfoRow label="iLvl" value={user.config.ilvl?.length ? user.config.ilvl.join(' – ') : '—'} />
                </>
              ) : (
                <EmptyState text="No filter configuration" />
              )}
            </SectionCard>

            {/* ── Data Configuration ── */}
            <SectionCard title="Data Configuration" icon={<ShieldCheckIcon className="h-4 w-4 text-gray-500" />} defaultOpen={false}>
              {user.dataConfiguration ? (
                <>
                  <InfoRow label="Drop Ship Count" value={user.dataConfiguration.dropShipCount} />
                  <InfoRow label="Stored Filters" value={user.dataConfiguration.storedFilters} />
                  <InfoRow label="Selling Realms" value={user.dataConfiguration.sellingRealms} />
                </>
              ) : (
                <EmptyState text="No data configuration" />
              )}
            </SectionCard>

            {/* ── Snipe Config ── */}
            <SectionCard title="Snipe Configuration" icon={<BellAlertIcon className="h-4 w-4 text-gray-500" />} defaultOpen={false}>
              {user.snipeConfig ? (
                <>
                  <InfoRow label="Enabled" value={user.snipeConfig.enabled ? 'Yes' : 'No'} />
                  <InfoRow label="Alert Style" value={user.snipeConfig.alertStyle || 'default'} />
                  <InfoRow label="Realms" value={user.snipeConfig.realms?.length ? user.snipeConfig.realms.join(', ') : '—'} />

                  {user.snipeConfig.items && user.snipeConfig.items.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Snipe Items ({user.snipeConfig.items.length})</p>
                      <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-400">
                              <th className="text-left pb-1">Item ID</th>
                              <th className="text-right pb-1">Max Price</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {user.snipeConfig.items.map((item, i) => (
                              <tr key={i}>
                                <td className="py-1 font-mono text-gray-700">{item.itemId}</td>
                                <td className="py-1 text-right text-gray-700">{formatGold(item.price)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {user.snipeConfig.pets && user.snipeConfig.pets.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Snipe Pets ({user.snipeConfig.pets.length})</p>
                      <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-400">
                              <th className="text-left pb-1">Pet ID</th>
                              <th className="text-right pb-1">Max Price</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {user.snipeConfig.pets.map((pet, i) => (
                              <tr key={i}>
                                <td className="py-1 font-mono text-gray-700">{pet.itemId}</td>
                                <td className="py-1 text-right text-gray-700">{formatGold(pet.price)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <EmptyState text="No snipe configuration" />
              )}
            </SectionCard>

            {/* ── Favorites ── */}
            <SectionCard title={`Favorites (${user.favorites?.length || 0})`} icon={<HeartIcon className="h-4 w-4 text-gray-500" />} defaultOpen={false}>
              {user.favorites && user.favorites.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {user.favorites.map((fav, i) => (
                    <span key={i} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-mono border border-gray-200">
                      {fav}
                    </span>
                  ))}
                </div>
              ) : (
                <EmptyState text="No favorites" />
              )}
            </SectionCard>

            {/* ── Login-with-token Preview ── */}
            <SectionCard
              title="Login-with-Token Preview"
              icon={<CodeBracketIcon className="h-4 w-4 text-gray-500" />}
              defaultOpen={false}
            >
              {tokenPreviewLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Loading token payload…
                </div>
              ) : tokenPreview ? (
                <div className="relative">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(tokenPreview, null, 2));
                      setTokenPreviewCopied(true);
                      setTimeout(() => setTokenPreviewCopied(false), 2000);
                    }}
                    className="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded border border-gray-200 transition-colors"
                  >
                    {tokenPreviewCopied ? '✓ Copied' : 'Copy JSON'}
                  </button>
                  <pre className="text-xs bg-gray-950 text-green-400 rounded-lg p-4 overflow-x-auto max-h-96 overflow-y-auto font-mono leading-relaxed">
                    {JSON.stringify(tokenPreview, null, 2)}
                  </pre>
                </div>
              ) : (
                <EmptyState text="Token preview unavailable" />
              )}
            </SectionCard>
          </div>
        )}
      </div>
    </div>
        </div>
      </div>
    </div>
  );
};

export default UserLookup;